import type { ReactiveController, ReactiveControllerHost } from 'lit';

interface CommentsSheetControllerOptions {
    /** Called when an external event requests opening the sheet. */
    onOpen: () => void;
}

/**
 * Reactive controller that owns the mobile comments-sheet's open-by-event
 * lifecycle on behalf of `<vm-video-comments>`. Listens on `document` for
 * `vm-video-comments-button:open` (dispatched by `<vm-video-comments-button>`)
 * and invokes `onOpen` to flip the host's sheet-open state.
 *
 * Lives as a controller (not inside the host) so the listener is registered
 * once per host and torn down on disconnect — no leak risk on reattach, and
 * the host's render stays focused on layout.
 */
export class CommentsSheetController implements ReactiveController {
    private _options: CommentsSheetControllerOptions;

    constructor(host: ReactiveControllerHost, options: CommentsSheetControllerOptions) {
        this._options = options;
        host.addController(this);
    }

    hostConnected(): void {
        document.addEventListener('vm-video-comments-button:open', this._onOpenEvent);
    }

    hostDisconnected(): void {
        document.removeEventListener('vm-video-comments-button:open', this._onOpenEvent);
    }

    private _onOpenEvent = (e: Event): void => {
        if (e.defaultPrevented) return;
        this._options.onOpen();
    };
}
